import { createSlice } from "@reduxjs/toolkit";

const MAX_OPEN_WINDOWS = 3;

const chatPopupSlice = createSlice({
  name: "chatPopup",
  initialState: {
    isListOpen: false,
    openWindows: [], // [{ conversationId, minimized }]
    // Popup conversation list (independent from full-page messenger)
    conversations: {
      ids: [],
      entities: {},
      page: 1,
      hasMore: true,
    },
    isGettingList: false,
    // Per-window message state keyed by conversationId
    windowMessages: {},
    // Per-window conversation detail for header info
    windowConversationDetails: {},
    // Unread count for floating icon badge
    fonehouseUnreadCount: 0,
    // Last sent message for socket emit
    lastSentMessage: null,
    // Sending state per window
    sendingWindows: {}, // { [conversationId]: boolean }
  },
  reducers: {
    toggleList(state) {
      state.isListOpen = !state.isListOpen;
    },
    closeList(state) {
      state.isListOpen = false;
    },
    openChatWindow(state, action) {
      const { conversationId } = action.payload;
      // Don't open duplicate
      if (state.openWindows.some((w) => w.conversationId === conversationId)) {
        // Un-minimize if already open
        state.openWindows = state.openWindows.map((w) =>
          w.conversationId === conversationId
            ? { ...w, minimized: false }
            : w
        );
        return;
      }
      // Remove oldest if at max
      if (state.openWindows.length >= MAX_OPEN_WINDOWS) {
        const removed = state.openWindows.shift();
        delete state.windowMessages[removed.conversationId];
      }
      state.openWindows.push({ conversationId, minimized: false });
      // Initialize message state
      if (!state.windowMessages[conversationId]) {
        state.windowMessages[conversationId] = {
          ids: [],
          entities: {},
          hasMore: true,
          isLoading: false,
          lastMessageTime: null,
        };
      }
    },
    closeChatWindow(state, action) {
      const { conversationId } = action.payload;
      state.openWindows = state.openWindows.filter(
        (w) => w.conversationId !== conversationId
      );
      delete state.windowMessages[conversationId];
    },
    minimizeChatWindow(state, action) {
      const { conversationId } = action.payload;
      state.openWindows = state.openWindows.map((w) =>
        w.conversationId === conversationId
          ? { ...w, minimized: !w.minimized }
          : w
      );
    },
    setUnreadCount(state, action) {
      state.fonehouseUnreadCount = action.payload;
    },
    // Conversation list
    popupGetConversationListRequest(state) {
      state.isGettingList = true;
    },
    popupGetConversationListSuccess(state, action) {
      const { list, page, hasNextPage } = action.payload;
      state.isGettingList = false;
      if (page === 1) {
        const entities = {};
        const ids = [];
        list.forEach((conv) => {
          entities[conv._id] = conv;
          ids.push(conv._id);
        });
        state.conversations.entities = entities;
        state.conversations.ids = ids;
        state.conversations.page = 1;
      } else {
        if (list.length > 0) {
          list.forEach((conv) => {
            if (!state.conversations.entities[conv._id]) {
              state.conversations.ids.push(conv._id);
            }
            state.conversations.entities[conv._id] = conv;
          });
          state.conversations.page += 1;
        }
      }
      state.conversations.hasMore =
        hasNextPage ?? (list.length > 0 && list.length >= 30);
    },
    popupStopLoading(state) {
      state.isGettingList = false;
    },
    // Per-window messages
    popupGetMessageListRequest(state, action) {
      const { conversationId } = action.payload;
      if (state.windowMessages[conversationId]) {
        state.windowMessages[conversationId].isLoading = true;
      }
    },
    popupGetMessageListSuccess(state, action) {
      const { conversationId, list, time } = action.payload;
      if (!state.windowMessages[conversationId]) {
        state.windowMessages[conversationId] = {
          ids: [],
          entities: {},
          hasMore: true,
          isLoading: false,
          lastMessageTime: null,
        };
      }
      const wm = state.windowMessages[conversationId];
      wm.isLoading = false;
      if (!time) {
        // First load - replace all
        const entities = {};
        const ids = [];
        list.forEach((msg) => {
          entities[msg._id] = msg;
          ids.push(msg._id);
        });
        wm.entities = entities;
        wm.ids = ids;
      } else {
        // Load more - append
        list.forEach((msg) => {
          if (!wm.entities[msg._id]) {
            wm.ids.push(msg._id);
          }
          wm.entities[msg._id] = msg;
        });
      }
      const lastMessage = list[list.length - 1];
      if (lastMessage) {
        wm.lastMessageTime = new Date(lastMessage.createdAt).getTime();
      }
      wm.hasMore = list.length >= 15;
    },
    // Send message
    popupSendMessageRequest(state, action) {
      const { conversationId } = action.payload;
      state.sendingWindows[conversationId] = true;
    },
    popupSendMessageSuccess(state, action) {
      const { conversationId, message } = action.payload;
      state.sendingWindows[conversationId] = false;
      if (message) {
        state.lastSentMessage = {
          conversationId: message.conversationId,
          messageId: message._id,
        };
        // Add message to window
        const wm = state.windowMessages[conversationId];
        if (wm && !wm.entities[message._id]) {
          wm.ids.unshift(message._id);
          wm.entities[message._id] = message;
        }
      }
    },
    popupSendMessageFailure(state, action) {
      const { conversationId } = action.payload;
      state.sendingWindows[conversationId] = false;
    },
    // Seen conversation
    popupSeenConversationRequest() {},
    popupSeenConversationSuccess(state, action) {
      const { conversationId } = action.payload;
      const conv = state.conversations.entities[conversationId];
      if (conv) {
        state.conversations.entities[conversationId] = {
          ...conv,
          unseenMessages: 0,
        };
      }
    },
    // Handle incoming socket message for a popup window
    popupNewMessage(state, action) {
      const { conversationId, message } = action.payload;
      const wm = state.windowMessages[conversationId];
      if (wm) {
        // Check if message is a confirmation of a sent message (same text from admin)
        if (message.isAdmin === 1) {
          const existingIdx = wm.ids.findIndex((id) => {
            const existing = wm.entities[id];
            return (
              existing &&
              existing.text === message.text &&
              existing._id !== message._id &&
              existing.conversationId === message.conversationId
            );
          });
          if (existingIdx !== -1) {
            // Replace temp with server message
            const oldId = wm.ids[existingIdx];
            delete wm.entities[oldId];
            wm.ids[existingIdx] = message._id;
            wm.entities[message._id] = message;
            return;
          }
        }
        if (!wm.entities[message._id]) {
          wm.ids.unshift(message._id);
          wm.entities[message._id] = message;
        }
      }
    },
    // Update conversation detail in popup list
    popupUpdateConversation(state, action) {
      const { conversation } = action.payload;
      if (conversation && state.conversations.entities[conversation._id]) {
        state.conversations.entities[conversation._id] = conversation;
      }
      // Also update window detail if open
      if (
        conversation &&
        state.windowConversationDetails[conversation._id]
      ) {
        state.windowConversationDetails[conversation._id] = conversation;
      }
    },
    // Set window conversation detail
    popupSetConversationDetail(state, action) {
      const { conversationId, detail } = action.payload;
      state.windowConversationDetails[conversationId] = detail;
      // Also update in conversations list
      if (state.conversations.entities[conversationId]) {
        state.conversations.entities[conversationId] = detail;
      }
    },
    // Mark message as recalled
    popupMarkMessageRecalled(state, action) {
      const { messageId } = action.payload;
      // Search in all open windows
      Object.values(state.windowMessages).forEach((wm) => {
        if (wm.entities[messageId]) {
          wm.entities[messageId] = {
            ...wm.entities[messageId],
            isRecalled: true,
            text: null,
            attachments: [],
          };
        }
      });
    },
    // Update message text
    popupUpdateMessageText(state, action) {
      const { messageId, text, isEdited, editedAt } = action.payload;
      Object.values(state.windowMessages).forEach((wm) => {
        if (wm.entities[messageId]) {
          wm.entities[messageId] = {
            ...wm.entities[messageId],
            text,
            isEdited,
            editedAt,
          };
        }
      });
    },
    // Update user online/offline status
    popupUpdateUserStatus(state, action) {
      const { userId, online, lastSeen } = action.payload;
      // Update in conversation list
      state.conversations.ids.forEach((id) => {
        const conv = state.conversations.entities[id];
        if (conv?.users) {
          const updatedUsers = conv.users.map((user) =>
            user.id === userId ? { ...user, online, lastSeen } : user
          );
          state.conversations.entities[id] = {
            ...conv,
            users: updatedUsers,
          };
        }
      });
      // Update in window details
      Object.keys(state.windowConversationDetails).forEach((convId) => {
        const conv = state.windowConversationDetails[convId];
        if (conv?.users) {
          const updatedUsers = conv.users.map((user) =>
            user.id === userId ? { ...user, online, lastSeen } : user
          );
          state.windowConversationDetails[convId] = {
            ...conv,
            users: updatedUsers,
          };
        }
      });
    },
    // Increment unseen for badge
    popupIncrementUnseen(state, action) {
      const { conversationId } = action.payload;
      const conv = state.conversations.entities[conversationId];
      if (conv) {
        state.conversations.entities[conversationId] = {
          ...conv,
          unseenMessages: (conv.unseenMessages || 0) + 1,
        };
      }
    },
  },
});

export const {
  toggleList,
  closeList,
  openChatWindow,
  closeChatWindow,
  minimizeChatWindow,
  setUnreadCount,
  popupGetConversationListRequest,
  popupGetConversationListSuccess,
  popupStopLoading,
  popupGetMessageListRequest,
  popupGetMessageListSuccess,
  popupSendMessageRequest,
  popupSendMessageSuccess,
  popupSendMessageFailure,
  popupSeenConversationRequest,
  popupSeenConversationSuccess,
  popupNewMessage,
  popupUpdateConversation,
  popupSetConversationDetail,
  popupMarkMessageRecalled,
  popupUpdateMessageText,
  popupUpdateUserStatus,
  popupIncrementUnseen,
} = chatPopupSlice.actions;

export default chatPopupSlice.reducer;
